import { useState, useRef } from 'react';

const FONT = "'DM Sans', sans-serif";
const ALLOWED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_INPUT_BYTES = 20 * 1024 * 1024;
const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.85;

type Props = {
  slug: string;
  initialFotoUrl?: string | null;
  nomeHomenageada?: string;
};

export default function PhotoUpload({ slug, initialFotoUrl, nomeHomenageada }: Props) {
  const [fotoUrl, setFotoUrl] = useState<string | null>(initialFotoUrl || null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setError(null);
    if (!ALLOWED.includes(file.type)) {
      setError('Use uma imagem JPG, PNG ou WebP.');
      return;
    }
    if (file.size > MAX_INPUT_BYTES) {
      setError('A foto precisa ter no máximo 20MB.');
      return;
    }
    setUploading(true);
    try {
      const base64 = await resizeAndEncode(file);
      const r = await fetch('/api/checkout?action=upload-foto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, foto_base64: base64, mime_type: 'image/jpeg' }),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok || !data?.foto_url) {
        setError(data?.error || 'Não consegui salvar a foto. Tenta de novo?');
        return;
      }
      setFotoUrl(data.foto_url);
    } catch {
      setError('Erro ao processar a foto. Tenta outra?');
    } finally {
      setUploading(false);
    }
  };

  const handlePickFile = () => inputRef.current?.click();

  const handleRemove = async () => {
    setUploading(true);
    setError(null);
    try {
      await fetch('/api/checkout?action=upload-foto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, remove: true }),
      });
      setFotoUrl(null);
    } catch {
      setFotoUrl(null);
    } finally {
      setUploading(false);
    }
  };

  return (
    <section
      style={{
        marginTop: 18,
        padding: '18px 20px',
        borderRadius: 18,
        background: 'var(--surface-white)',
        border: '1.5px dashed var(--border-soft)',
        fontFamily: FONT,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div
          style={{
            flexShrink: 0,
            width: 64,
            height: 64,
            borderRadius: 16,
            overflow: 'hidden',
            background: 'var(--gold-pale)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid var(--border-soft)',
          }}
        >
          {fotoUrl ? (
            <img src={fotoUrl} alt="Capa" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <span aria-hidden style={{ fontSize: 28 }}>📷</span>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--heading-ink)', marginBottom: 2, letterSpacing: '-0.01em' }}>
            Capa personalizada {fotoUrl ? '✓' : '(opcional)'}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {fotoUrl
              ? `Sua foto será a capa da música${nomeHomenageada ? ` pra ${nomeHomenageada}` : ''}.`
              : 'Envie uma foto que ficará como capa da música. Se não enviar, usamos o logo da Inspira.'}
          </div>
        </div>
      </div>

      {error && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: '#9F2A1F' }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button
          type="button"
          onClick={handlePickFile}
          disabled={uploading}
          style={{
            flex: 1,
            padding: '11px 14px',
            borderRadius: 999,
            background: fotoUrl ? 'transparent' : 'var(--gold-pale)',
            color: 'var(--amber-deep)',
            border: '1.5px solid var(--gold-border)',
            fontSize: 13,
            fontWeight: 700,
            cursor: uploading ? 'not-allowed' : 'pointer',
            fontFamily: FONT,
            opacity: uploading ? 0.6 : 1,
            transition: 'all 0.18s',
          }}
        >
          {uploading ? 'Enviando…' : fotoUrl ? '🔄 Trocar foto' : '📤 Escolher foto'}
        </button>
        {fotoUrl && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={uploading}
            style={{
              padding: '11px 14px',
              borderRadius: 999,
              background: 'transparent',
              color: 'var(--text-muted)',
              border: '1.5px solid var(--border-soft)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: FONT,
            }}
          >
            Remover
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = '';
        }}
        style={{ display: 'none' }}
      />
    </section>
  );
}

function resizeAndEncode(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const ratio = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('canvas-unsupported'));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('blob-failed'));
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1] || '');
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        },
        'image/jpeg',
        JPEG_QUALITY,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image-load-failed'));
    };
    img.src = url;
  });
}
